// Medical Portal — Reports & Analytics
// Two sub-tabs:
//   • TAT Analysis      — KPIs, daily trend bars, per-test TAT, report-level table
//   • Complaints Analysis — KPIs, by-type bars, response KPIs, outcomes, table
import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Skeleton from '@mui/material/Skeleton';
import {
  MP, MONO, DISPLAY,
  SectionCard, TabButton, Pill, StatusBadge,
  ScrollTable, Th, Td, Tr,
} from './shared';
import {
  useGetTatReportQuery,
  useGetTatReportByCustomerAndDoctorQuery,
} from '../../../store/tatReportApi';
import { useListRcaInvestigationsQuery } from '../../../store/queriesApi';
import { useAuth } from '../../../context/AuthContext';

// ─── TAT helpers ────────────────────────────────────────────────────
// IT Dose ships hours as zero-padded "HH:MM:SS" strings — sometimes
// "07:09:52", sometimes "00:01:20", occasionally "19:19:38". Convert
// to total minutes for averaging; return null for missing / malformed
// values so they don't pollute the mean.
const parseHrsMinSec = (raw) => {
  const m = String(raw || '').trim().match(/^(\d+):(\d{2}):(\d{2})$/);
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]) + Number(m[3]) / 60;
};

// "01-Apr-2026 01:07AM" → Date. Used for daily-trend grouping. Returns
// null on parse failure (we just skip the row).
const MONTHS = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5,
                 Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };
const parseItdoseDateTime = (raw) => {
  const m = String(raw || '').trim().match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)?$/i);
  if (!m) return null;
  const mon = MONTHS[m[2][0].toUpperCase() + m[2].slice(1, 3).toLowerCase()];
  if (mon === undefined) return null;
  let hr = Number(m[4]);
  const ampm = (m[6] || '').toUpperCase();
  if (ampm === 'PM' && hr < 12) hr += 12;
  if (ampm === 'AM' && hr === 12) hr = 0;
  return new Date(Number(m[3]), mon, Number(m[1]), hr, Number(m[5]));
};

// "153 min" → "2h 33m". For minutes < 60 we still emit "Xh Ym" so
// every tile reads as a TAT block (consistent visual rhythm).
const formatMinutes = (mins) => {
  if (mins == null || !Number.isFinite(mins)) return '—';
  const h = Math.floor(mins / 60);
  const m = Math.round(mins % 60);
  return `${h}h ${String(m).padStart(2, '0')}m`;
};

// "01-Apr-2026 01:07AM" → "1 Apr". For the daily trend x-axis.
const shortDate = (d) => d
  ? `${d.getDate()} ${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()]}`
  : '—';
const dayKey = (d) => d
  ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  : '';

// Default the picker to the last 7 days *ending yesterday* — today's
// samples are still being collected (morning draws don't get approved
// until evening), so including them would skew TAT averages with
// half-finished records. IT Dose caps the window at 7 days inclusive,
// so `yesterday` back to `yesterday - 6` gives exactly 7 days without
// breaching the cap.
const ymd = (d) => d.toISOString().split('T')[0];
const MAX_INCLUSIVE_DAYS = 7;
const lastNDaysEndingYesterday = (n) => {
  const end = new Date();
  end.setDate(end.getDate() - 1);            // yesterday
  const start = new Date(end);
  start.setDate(end.getDate() - (n - 1));    // n days inclusive
  return { from: ymd(start), to: ymd(end) };
};
const defaultRange = () => lastNDaysEndingYesterday(MAX_INCLUSIVE_DAYS);

// Inclusive day count between two YYYY-MM-DD strings — both ends
// counted. Used to gate the Apply button so the user can't fire a
// guaranteed-failure request.
const inclusiveDays = (from, to) => {
  if (!from || !to) return 0;
  const a = new Date(from);
  const b = new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b < a) return 0;
  return Math.round((b - a) / 86400000) + 1;
};

const Analytics = () => {
  const [tab, setTab] = useState('tat');
  const initial = useMemo(defaultRange, []);
  // `from`/`to` track the picker; `applied.*` is what the TAT panel
  // actually queries. Splitting them lets the user tweak the inputs
  // without re-firing a 7 MB request until they click Apply.
  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [applied, setApplied] = useState(initial);

  // `days` is the inclusive-window size ENDING YESTERDAY (today's
  // partial data is excluded — see defaultRange comment). Caps at
  // MAX_INCLUSIVE_DAYS so the chip can never request more than the
  // upstream allows.
  const quick = (days) => {
    const next = lastNDaysEndingYesterday(Math.min(days, MAX_INCLUSIVE_DAYS));
    setFrom(next.from);
    setTo(next.to);
    setApplied(next);
  };

  const span = inclusiveDays(from, to);
  const rangeTooWide = span > MAX_INCLUSIVE_DAYS;
  const rangeInvalid = span === 0;
  const apply = () => {
    if (rangeTooWide || rangeInvalid) return;
    setApplied({ from, to });
  };

  return (
    <Box>
      {/* ── Date-range filter ── */}
      <SectionCard sx={{ mb: 2.5 }}>
        <Grid container spacing={1.25} sx={{ p: 2, alignItems: 'center' }}>
          <Grid item xs={12} md="auto">
            <Typography sx={{ fontFamily: DISPLAY, fontSize: 13.5, fontWeight: 700, letterSpacing: '.02em', color: MP.dark }}>
              Analysis Period
            </Typography>
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              size="small" type="date" fullWidth
              value={from} onChange={(e) => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }} label="From" sx={filterInputSx}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              size="small" type="date" fullWidth
              value={to} onChange={(e) => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }} label="To" sx={filterInputSx}
            />
          </Grid>
          <Grid item xs={12} sm={6} md>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Button size="small" variant="outlined" onClick={() => quick(7)}>Last 7 days</Button>
              {/* IT Dose caps the window at 7 days — show why the
                  range slider doesn't go wider, so users don't keep
                  typing a 30-day range and wondering why it fails.
                  Also flags that today is excluded (samples still in
                  flight would skew the averages). */}
              <Typography sx={{ fontSize: 11, color: MP.textMuted, fontStyle: 'italic' }}>
                Up to {MAX_INCLUSIVE_DAYS} days · today excluded
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md="auto">
            <Button
              size="small" variant="contained" fullWidth
              onClick={apply}
              disabled={rangeTooWide || rangeInvalid}
              sx={{
                bgcolor: MP.teal,
                color: MP.white,
                '&:hover': { bgcolor: MP.tealDark },
                '&.Mui-disabled': { bgcolor: MP.border, color: MP.textMuted },
              }}
            >
              Apply →
            </Button>
            {rangeTooWide && (
              <Typography sx={{ fontSize: 11, color: MP.red, mt: 0.5, textAlign: 'right' }}>
                Range too wide ({span} days). Max {MAX_INCLUSIVE_DAYS}.
              </Typography>
            )}
          </Grid>
        </Grid>
      </SectionCard>

      {/* ── Sub-tabs ── */}
      <Stack direction="row" spacing={0.5} sx={{ overflowX: 'auto' }}>
        <TabButton active={tab === 'tat'} onClick={() => setTab('tat')}>⏱ TAT Analysis</TabButton>
        <TabButton active={tab === 'cmp'} onClick={() => setTab('cmp')}>⚠ Complaints Analysis</TabButton>
      </Stack>

      <Box sx={{
        border: `1.5px solid ${MP.border}`, borderRadius: '0 6px 6px 6px',
        bgcolor: MP.white, overflow: 'hidden',
      }}>
        {tab === 'tat'
          ? <TATPanel fromDate={applied.from} toDate={applied.to} />
          : <ComplaintsPanel fromDate={applied.from} toDate={applied.to} />}
      </Box>
    </Box>
  );
};

// ───────────────────── TAT PANEL ─────────────────────
// All five KPIs + the two charts + the report-level table derive
// from the IT Dose GetTATReport feed. Backend scopes records to the
// caller's client_id (+ Doctor_ID for doctor sessions) so the same
// component renders the right slice for centre admins and doctors
// without us branching here.
const TATPanel = ({ fromDate, toDate }) => {
  // Same branching pattern as patient-reports + the dashboard TAT
  // card: both hooks always mount, only one fires based on the
  // signed-in user_type. Backend enforces JWT-side doctor scope on
  // top of this so a doctor can't widen the slice by hand.
  const { user } = useAuth();
  const isDoctor = user?.user_type === 'doctor';
  const itdoseDoctorId = user?.doctor?.itdose_doctor_id;

  // Bumped by the Refresh button. Both layers cache — RTK Query by args,
  // the backend by date range — so a changed value is what gets past both.
  const [refresh, setRefresh] = useState(0);

  const centreQuery = useGetTatReportQuery(
    { fromDate, toDate, refresh },
    { skip: !fromDate || !toDate || isDoctor },
  );
  const doctorQuery = useGetTatReportByCustomerAndDoctorQuery(
    { fromDate, toDate, doctorId: itdoseDoctorId, refresh },
    { skip: !fromDate || !toDate || !isDoctor || !itdoseDoctorId },
  );
  const { data, isLoading, isFetching, isError, error } = isDoctor
    ? doctorQuery
    : centreQuery;
  const rows = useMemo(() => data?.data || [], [data]);

  // Top-line KPIs.
  const kpis = useMemo(() => {
    const tats = [];
    let inTat = 0;
    let outTat = 0;
    let approvedCount = 0;
    for (const r of rows) {
      const mins = parseHrsMinSec(r?.['Booking-Approval(hrs)']);
      if (mins != null) tats.push(mins);
      const tag = String(r?.INOutTAT || '').trim().toLowerCase();
      if (tag === 'in tat') inTat += 1;
      else if (tag === 'out tat') outTat += 1;
      if (String(r?.LastStatus || '').trim().toLowerCase() === 'approved') approvedCount += 1;
    }
    const total = rows.length;
    const avgTat = tats.length ? tats.reduce((a, b) => a + b, 0) / tats.length : null;
    const inOutTotal = inTat + outTat;
    const onTimePct = inOutTotal ? Math.round((inTat / inOutTotal) * 100) : null;
    return { total, avgTat, onTimePct, breaches: outTat, approved: approvedCount };
  }, [rows]);

  // Daily TAT trend — group by BookingDate's date portion, average
  // delivered TAT per day. Sorted chronologically so the bars read
  // left → right oldest → newest.
  const dailyTrend = useMemo(() => {
    const byDay = new Map();
    for (const r of rows) {
      const d = parseItdoseDateTime(r?.BookingDate);
      const mins = parseHrsMinSec(r?.['Booking-Approval(hrs)']);
      if (!d || mins == null) continue;
      const k = dayKey(d);
      const entry = byDay.get(k) || { sum: 0, count: 0, date: d };
      entry.sum   += mins;
      entry.count += 1;
      byDay.set(k, entry);
    }
    const arr = [...byDay.values()]
      .map((e) => ({ date: e.date, avgMins: e.sum / e.count }))
      .sort((a, b) => a.date - b.date);
    const max = arr.reduce((m, e) => Math.max(m, e.avgMins), 0) || 1;
    // Tone: green ≤120 min, orange ≤240, red beyond.
    const toneFor = (m) => m <= 120 ? MP.teal : m <= 240 ? MP.orange : MP.red;
    return arr.map((e) => ({
      label: shortDate(e.date),
      avgMins: e.avgMins,
      pct: Math.max(8, Math.round((e.avgMins / max) * 100)),
      tone: toneFor(e.avgMins),
    }));
  }, [rows]);

  // TAT by Test Type — top 7 tests by volume so the chart stays
  // readable. For each, show avg delivered TAT.
  const byTest = useMemo(() => {
    const byName = new Map();
    for (const r of rows) {
      const name = String(r?.TestName || '').trim();
      const mins = parseHrsMinSec(r?.['Booking-Approval(hrs)']);
      if (!name || mins == null) continue;
      const entry = byName.get(name) || { sum: 0, count: 0 };
      entry.sum   += mins;
      entry.count += 1;
      byName.set(name, entry);
    }
    const arr = [...byName.entries()]
      .map(([name, e]) => ({ name, avgMins: e.sum / e.count, count: e.count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 7);
    const max = arr.reduce((m, e) => Math.max(m, e.avgMins), 0) || 1;
    const toneFor = (m) => m <= 120 ? MP.green : m <= 180 ? MP.teal : m <= 300 ? MP.orange : MP.red;
    return arr.map((e) => ({
      name: e.name,
      label: formatMinutes(e.avgMins),
      pct: Math.max(6, Math.round((e.avgMins / max) * 100)),
      tone: toneFor(e.avgMins),
      count: e.count,
    }));
  }, [rows]);

  // Report-level table — last 50 rows sorted newest-first by
  // BookingDate. We only surface the columns the centre / doctor
  // actually scans for: who, what, when, how long, in or out of TAT.
  const tableRows = useMemo(() => {
    return [...rows]
      .map((r) => ({
        r,
        when: parseItdoseDateTime(r?.BookingDate),
        deliveredMins: parseHrsMinSec(r?.['Booking-Approval(hrs)']),
      }))
      .sort((a, b) => (b.when?.getTime() || 0) - (a.when?.getTime() || 0))
      .slice(0, 50);
  }, [rows]);

  // Empty / error states. We don't show the full skeleton + then a
  // toast — just one branched message so the page never looks broken.
  // `isFetching` covers refetches (when the user picks a new range),
  // `isLoading` covers the first-ever fetch. Both should swap the
  // panel out for the skeleton so the numbers/charts never display
  // stale values from the previous range while the new data is in
  // flight.
  if (isLoading || isFetching) return <SkeletonPanel />;
  if (isError) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: MP.textMuted, fontSize: 13 }}>
        Could not load TAT data.<br />
        <Typography sx={{ fontFamily: MONO, fontSize: 11, mt: 0.75, color: MP.textMuted }}>
          {error?.data?.message || error?.error || 'Try again in a moment.'}
        </Typography>
      </Box>
    );
  }
  if (!rows.length) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: MP.textMuted, fontSize: 13 }}>
        No reports in the selected period.<br />
        <Typography sx={{ fontSize: 11, color: MP.textMuted, mt: 0.5 }}>
          Pick a wider range or check that your account is linked to a centre.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Freshness + manual refresh.
          The numbers below are served from a cache — a day old at most for
          a finished period, an hour for one that includes today. That is
          what makes the screen open instantly instead of waiting ~15s on
          IT Dose. This row says so, and gives a way to go and ask them
          again when you need to be certain right now. Nothing refreshes on
          a timer; this button is the only way it happens. */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1.5}
        sx={{ px: 2.25, py: 1.25, borderBottom: `1px solid ${MP.border}`, bgcolor: MP.bg }}
      >
        <Typography sx={{ fontSize: 11.5, color: MP.ink3 }}>
          Served from cache for speed — refresh to pull the latest from the lab system.
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setRefresh((n) => n + 1)}
          disabled={isFetching}
          sx={{
            textTransform: 'none', fontWeight: 500, fontSize: 12.5,
            borderRadius: MP.radius, borderColor: MP.border, color: MP.textMuted,
            whiteSpace: 'nowrap', flexShrink: 0,
            '&:hover': { borderColor: MP.teal, color: MP.teal, bgcolor: MP.white },
          }}
        >
          {isFetching ? 'Refreshing…' : 'Refresh'}
        </Button>
      </Stack>

      {/* KPIs */}
      <Grid container sx={{ borderBottom: `1px solid ${MP.border}` }}>
        {[
          ['Total Reports',   String(kpis.total),                     'In selected period',  MP.dark],
          ['Avg Delivered TAT', formatMinutes(kpis.avgTat),            'Booking → Approval',  MP.teal],
          ['On-Time Delivery',  kpis.onTimePct == null ? '—' : `${kpis.onTimePct}%`, 'INOutTAT = In TAT', MP.green],
          ['TAT Breaches',      String(kpis.breaches),                  'Out-TAT records',     MP.red],
          ['Approved Reports',  String(kpis.approved),                  'LastStatus = Approved', MP.orange],
        ].map(([label, value, sub, color], i, arr) => (
          <Grid
            item xs={6} sm={4} md={12 / 5}
            key={label}
            sx={{ p: 2.25, borderRight: { sm: i < arr.length - 1 ? `1px solid ${MP.border}` : 'none' } }}
          >
            <Typography sx={{
              fontSize: 10, color: MP.textMuted, textTransform: 'uppercase',
              letterSpacing: '0.1em', fontFamily: MONO, mb: 0.75,
            }}>
              {label}
            </Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 28, fontWeight: 600, letterSpacing: '-.02em', fontVariantNumeric: 'tabular-nums', color, lineHeight: 1 }}>
              {value}
            </Typography>
            <Typography sx={{ fontSize: 11, color: MP.textMuted, mt: 0.5 }}>{sub}</Typography>
          </Grid>
        ))}
      </Grid>

      {/* Daily trend + by-test */}
      <Grid container sx={{ borderBottom: `1px solid ${MP.border}` }}>
        <Grid item xs={12} md={7} sx={{ p: 2.5, borderRight: { md: `1px solid ${MP.border}` } }}>
          <Typography sx={{ fontFamily: DISPLAY, fontSize: 13.5, fontWeight: 700, letterSpacing: '.02em', color: MP.dark }}>
            Daily TAT Trend{' '}
            <Box component="span" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400, fontSize: 12, color: MP.textMuted }}>
              (Avg Booking → Approval per day)
            </Box>
          </Typography>
          {dailyTrend.length ? (
            <Stack direction="row" spacing={0.625} alignItems="flex-end" sx={{ height: 140, mt: 2 }}>
              {dailyTrend.map((b, i) => (
                <Stack key={i} flex={1} alignItems="center" spacing={0.375} sx={{ height: '100%', justifyContent: 'flex-end' }}>
                  <Box
                    title={`${b.label}: ${formatMinutes(b.avgMins)}`}
                    sx={{
                      width: '100%',
                      bgcolor: b.tone,
                      borderRadius: '6px 6px 0 0',
                      height: `${b.pct}%`,
                      opacity: 0.85,
                    }}
                  />
                  <Typography sx={{
                    fontSize: 9, fontFamily: MONO,
                    color: b.tone === MP.teal ? MP.textMuted : b.tone,
                    fontWeight: b.tone === MP.teal ? 400 : 700,
                  }}>
                    {b.label}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          ) : (
            <Typography sx={{ fontSize: 12, color: MP.textMuted, mt: 2 }}>
              No daily TAT data in this range.
            </Typography>
          )}
          <Stack direction="row" spacing={2} sx={{ mt: 1.5 }} flexWrap="wrap" rowGap={0.5}>
            <LegendSwatch color={MP.teal}>≤ 2 hrs</LegendSwatch>
            <LegendSwatch color={MP.orange}>2–4 hrs</LegendSwatch>
            <LegendSwatch color={MP.red}>&gt; 4 hrs</LegendSwatch>
          </Stack>
        </Grid>

        <Grid item xs={12} md={5} sx={{ p: 2.5 }}>
          <Typography sx={{ fontFamily: DISPLAY, fontSize: 13.5, fontWeight: 700, letterSpacing: '.02em', color: MP.dark, mb: 2 }}>
            TAT by Test Type{' '}
            <Box component="span" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400, fontSize: 12, color: MP.textMuted }}>
              (Top 7 by volume · Avg TAT)
            </Box>
          </Typography>
          {byTest.length ? (
            <Stack spacing={1.25}>
              {byTest.map((t) => (
                <Box key={t.name}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                    <Typography sx={{ fontSize: 12 }} title={`${t.count} reports`}>
                      {t.name}
                    </Typography>
                    <Typography sx={{ fontFamily: MONO, fontSize: 12, fontWeight: 700, color: t.tone }}>
                      {t.label}
                    </Typography>
                  </Stack>
                  <Box sx={{ height: 7, bgcolor: MP.bg, borderRadius: 99 }}>
                    <Box sx={{ width: `${t.pct}%`, height: '100%', bgcolor: t.tone, borderRadius: 99 }} />
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography sx={{ fontSize: 12, color: MP.textMuted }}>No tests in this range.</Typography>
          )}
        </Grid>
      </Grid>

      {/* Report-level table — last 50 rows newest first */}
      <Box sx={{ p: 2.5 }}>
        <Typography sx={{ fontFamily: DISPLAY, fontSize: 13.5, fontWeight: 700, letterSpacing: '.02em', color: MP.dark, mb: 1.75 }}>
          Report-Level TAT Detail{' '}
          <Box component="span" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400, fontSize: 12, color: MP.textMuted }}>
            (Showing {tableRows.length} of {rows.length})
          </Box>
        </Typography>
        <ScrollTable minW={900} maxH={360}>
          <thead>
            <tr>
              <Th>Date</Th>
              <Th>UHID</Th>
              <Th>Patient</Th>
              <Th>Test</Th>
              <Th>Department</Th>
              <Th>Doctor</Th>
              <Th align="center">Delivered TAT</Th>
              <Th align="center">In/Out TAT</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map(({ r, when, deliveredMins }) => {
              const tag = String(r?.INOutTAT || '').trim().toLowerCase();
              const inTone   = tag === 'in tat'  ? 'green' : tag === 'out tat' ? 'red' : 'yellow';
              const tatTone  = deliveredMins == null ? 'yellow'
                : deliveredMins <= 120 ? 'green'
                : deliveredMins <= 240 ? 'orange' : 'red';
              return (
                <Tr key={r.VisitNo + r.TestCode + r.SINNo}>
                  <Td sx={{ fontFamily: MONO, fontSize: 11, whiteSpace: 'nowrap' }}>{shortDate(when)}</Td>
                  <Td sx={{ fontFamily: MONO, color: MP.tealDark, fontSize: 12 }}>{r.UHID}</Td>
                  <Td>{r.PName}</Td>
                  <Td sx={{ fontSize: 12 }}>{r.TestName}</Td>
                  <Td sx={{ fontSize: 12, color: MP.textMuted }}>{r.DepartmentName}</Td>
                  <Td sx={{ fontSize: 12 }}>{r.DoctorName}</Td>
                  <Td sx={{ textAlign: 'center' }}>
                    <Pill tone={tatTone}>{formatMinutes(deliveredMins)}</Pill>
                  </Td>
                  <Td sx={{ textAlign: 'center' }}>
                    <Pill tone={inTone}>{r.INOutTAT || '—'}</Pill>
                  </Td>
                  <Td><StatusBadge>{r.LastStatus || '—'}</StatusBadge></Td>
                </Tr>
              );
            })}
          </tbody>
        </ScrollTable>
      </Box>
    </Box>
  );
};

// Lightweight loading placeholder — same shape as the real panel so
// the page height doesn't jump on first load. Used while the initial
// TAT fetch is in flight.
const SkeletonPanel = () => (
  <Box>
    <Grid container sx={{ borderBottom: `1px solid ${MP.border}` }}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Grid item xs={6} sm={4} md={12 / 5} key={i}
          sx={{ p: 2.25, borderRight: { sm: i < 4 ? `1px solid ${MP.border}` : 'none' } }}>
          <Skeleton variant="text" width={90} height={12} sx={{ mb: 1 }} />
          <Skeleton variant="text" width={80} height={40} />
          <Skeleton variant="text" width={120} height={12} sx={{ mt: 0.5 }} />
        </Grid>
      ))}
    </Grid>
    <Box sx={{ p: 2.5 }}>
      <Skeleton variant="rounded" width="100%" height={160} />
    </Box>
  </Box>
);

// ───────────────────── COMPLAINTS PANEL ─────────────────────
// Sourced from useListRcaInvestigationsQuery — same hook the Report
// Queries page uses. Backend auto-scopes for doctor sessions via the
// RcaQueryAuthor join we built, so the same component renders the
// right slice for centre admins and doctors with no branching here.
// Date filter is applied client-side on `created_at` (qtqms doesn't
// support a server-side date range, but ~hundreds of records is
// trivial to filter in JS).

// Open vs resolved bucketing. qtqms emits a small enum of labels and
// we match generously so any new variants don't silently drop into
// the wrong bucket.
const OPEN_LABELS = ['received', 'investigating', 'engaged'];
const RESOLVED_LABELS = ['resolved', 'closed'];

const isOpenComplaint = (r) => OPEN_LABELS.includes(String(r?.view_status_label || '').toLowerCase());
const isResolvedComplaint = (r) => RESOLVED_LABELS.includes(String(r?.view_status_label || '').toLowerCase());

// "2026-04-24T16:04:45+05:30" → minutes since epoch (or null). Used
// for response/resolution TAT means and for date-range filtering.
const tsMs = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
};

const ComplaintsPanel = ({ fromDate, toDate }) => {
  const { user } = useAuth();
  const clientId = user?.client_id;
  const { data, isLoading, isFetching, isError, error } = useListRcaInvestigationsQuery(
    { clientId },
    { skip: !clientId },
  );
  const allRecords = useMemo(() => data?.records || [], [data]);

  // Date-window slice — qtqms returns the full history, we trim to the
  // applied range on `created_at`. toDate is end-of-day inclusive so a
  // pick of "May 28" includes complaints raised at 11:59 PM.
  const rows = useMemo(() => {
    const fromMs = fromDate ? new Date(fromDate).getTime() : -Infinity;
    const toMsEnd = toDate
      ? new Date(toDate + 'T23:59:59.999').getTime()
      : Infinity;
    return allRecords.filter((r) => {
      const t = tsMs(r?.created_at);
      if (t == null) return false;
      return t >= fromMs && t <= toMsEnd;
    });
  }, [allRecords, fromDate, toDate]);

  // Top-line KPIs.
  const kpis = useMemo(() => {
    let open = 0;
    let resolved = 0;
    const ackMins = [];        // viewed_at − created_at
    const responseMins = [];   // engaged_at − created_at
    const resolutionMins = []; // root_cause_declared_at − created_at
    for (const r of rows) {
      if (isOpenComplaint(r)) open += 1;
      else if (isResolvedComplaint(r)) resolved += 1;
      const created = tsMs(r?.created_at);
      if (created == null) continue;
      const viewed = tsMs(r?.viewed_at);
      const engaged = tsMs(r?.engaged_at);
      const closed = tsMs(r?.root_cause_declared_at) ?? tsMs(r?.date_completed);
      if (viewed != null) ackMins.push((viewed - created) / 60000);
      if (engaged != null) responseMins.push((engaged - created) / 60000);
      if (closed != null) resolutionMins.push((closed - created) / 60000);
    }
    const total = rows.length;
    const mean = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
    const resolvedPct = total ? Math.round((resolved / total) * 100) : null;
    return {
      total,
      open,
      resolved,
      resolvedPct,
      avgResponse:    mean(responseMins),
      avgResolution:  mean(resolutionMins),
    };
  }, [rows]);

  // By Source — top 6 most common `source` values. Empty/blank
  // sources roll into an "Unspecified" bucket so the chart stays
  // honest without 12 long-tail entries.
  const bySource = useMemo(() => {
    const counts = new Map();
    for (const r of rows) {
      const src = String(r?.source || '').trim() || 'Unspecified';
      counts.set(src, (counts.get(src) || 0) + 1);
    }
    const arr = [...counts.entries()]
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
    const max = arr[0]?.count || 1;
    const palette = [MP.red, MP.orange, MP.yellow, MP.teal, MP.tealDark || MP.teal, MP.textMuted];
    return arr.map((e, i) => ({
      ...e,
      pct: Math.max(8, Math.round((e.count / max) * 100)),
      color: palette[i] || MP.textMuted,
    }));
  }, [rows]);

  // By Status — fixed buckets so the colours stay consistent across
  // ranges (even when one bucket is empty).
  const byStatus = useMemo(() => {
    const buckets = {
      received: 0,
      investigating: 0,
      resolved: 0,
      closed: 0,
    };
    for (const r of rows) {
      const l = String(r?.view_status_label || '').toLowerCase();
      if (l === 'received')        buckets.received      += 1;
      else if (l === 'investigating' || l === 'engaged') buckets.investigating += 1;
      else if (l === 'resolved')   buckets.resolved      += 1;
      else if (l === 'closed')     buckets.closed        += 1;
    }
    const total = rows.length || 1;
    return [
      { label: 'Received',      count: buckets.received,      pct: Math.round((buckets.received      / total) * 100), color: MP.red },
      { label: 'Investigating', count: buckets.investigating, pct: Math.round((buckets.investigating / total) * 100), color: MP.orange },
      { label: 'Resolved',      count: buckets.resolved,      pct: Math.round((buckets.resolved      / total) * 100), color: MP.green },
      { label: 'Closed',        count: buckets.closed,        pct: Math.round((buckets.closed        / total) * 100), color: MP.textMuted },
    ];
  }, [rows]);

  // Table rows — last 50 newest-first.
  const tableRows = useMemo(() => {
    return [...rows]
      .map((r) => ({
        r,
        createdMs: tsMs(r?.created_at) || 0,
        responseMins: (tsMs(r?.engaged_at) && tsMs(r?.created_at))
          ? (tsMs(r.engaged_at) - tsMs(r.created_at)) / 60000
          : null,
        resolutionMins: (() => {
          const created = tsMs(r?.created_at);
          const closed = tsMs(r?.root_cause_declared_at) ?? tsMs(r?.date_completed);
          if (created == null || closed == null) return null;
          return (closed - created) / 60000;
        })(),
      }))
      .sort((a, b) => b.createdMs - a.createdMs)
      .slice(0, 50);
  }, [rows]);

  if (isLoading || isFetching) return <SkeletonPanel />;
  if (isError) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: MP.textMuted, fontSize: 13 }}>
        Could not load complaints data.<br />
        <Typography sx={{ fontFamily: MONO, fontSize: 11, mt: 0.75, color: MP.textMuted }}>
          {error?.data?.message || error?.error || 'Try again in a moment.'}
        </Typography>
      </Box>
    );
  }
  if (!rows.length) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: MP.textMuted, fontSize: 13 }}>
        No complaints in the selected period.<br />
        <Typography sx={{ fontSize: 11, color: MP.textMuted, mt: 0.5 }}>
          Pick a wider range or check the Report Queries page.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* 5 KPIs */}
      <Grid container sx={{ borderBottom: `1px solid ${MP.border}` }}>
        {[
          ['Total Complaints',     String(kpis.total),                                 'In selected period',          MP.dark],
          ['Open / Pending',       String(kpis.open),                                  'Awaiting response',           MP.red],
          ['Resolved',             String(kpis.resolved),                              kpis.resolvedPct == null ? '—' : `${kpis.resolvedPct}% resolution rate`, MP.green],
          ['Avg Response',         formatMinutes(kpis.avgResponse),                    'Created → first engagement',  MP.teal],
          ['Avg Resolution TAT',   formatMinutes(kpis.avgResolution),                  'Created → closed (resolved only)', MP.orange],
        ].map(([label, value, sub, color], i, arr) => (
          <Grid
            item xs={6} sm={4} md={12 / 5}
            key={label}
            sx={{ p: 2.25, borderRight: { sm: i < arr.length - 1 ? `1px solid ${MP.border}` : 'none' } }}
          >
            <Typography sx={{
              fontSize: 10, color: MP.textMuted, textTransform: 'uppercase',
              letterSpacing: '0.1em', fontFamily: MONO, mb: 0.75,
            }}>
              {label}
            </Typography>
            <Typography sx={{ fontFamily: MONO, fontSize: 28, fontWeight: 600, letterSpacing: '-.02em', fontVariantNumeric: 'tabular-nums', color, lineHeight: 1 }}>
              {value}
            </Typography>
            <Typography sx={{ fontSize: 11, color: MP.textMuted, mt: 0.5 }}>{sub}</Typography>
          </Grid>
        ))}
      </Grid>

      {/* Breakdown — By Source (left) + By Status (right). Dropped the
          old 3-column block (Response Analysis + Outcomes) — those
          relied on SLA / outcome fields qtqms doesn't expose. Adding
          them back is a follow-up once the upstream surfaces SLA
          thresholds. */}
      <Grid container sx={{ borderBottom: `1px solid ${MP.border}` }}>
        <Grid item xs={12} md={6} sx={{ p: 2.5, borderRight: { md: `1px solid ${MP.border}` } }}>
          <Typography sx={{ fontFamily: DISPLAY, fontSize: 13.5, fontWeight: 700, letterSpacing: '.02em', color: MP.dark, mb: 1.75 }}>
            By Source{' '}
            <Box component="span" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400, fontSize: 12, color: MP.textMuted }}>
              (Top 6 reported sources)
            </Box>
          </Typography>
          {bySource.length ? (
            <Stack spacing={1.125}>
              {bySource.map((e) => (
                <Box key={e.label}>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.375, fontSize: 12 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{e.label}</Typography>
                    <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{e.count}</Typography>
                  </Stack>
                  <Box sx={{ height: 7, bgcolor: MP.bg, borderRadius: 99 }}>
                    <Box sx={{ width: `${e.pct}%`, height: '100%', bgcolor: e.color, borderRadius: 99 }} />
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography sx={{ fontSize: 12, color: MP.textMuted }}>No source data.</Typography>
          )}
        </Grid>

        <Grid item xs={12} md={6} sx={{ p: 2.5 }}>
          <Typography sx={{ fontFamily: DISPLAY, fontSize: 13.5, fontWeight: 700, letterSpacing: '.02em', color: MP.dark, mb: 1.75 }}>
            By Status
          </Typography>
          <Stack spacing={1.125}>
            {byStatus.map((e) => (
              <Box key={e.label}>
                <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.375, fontSize: 12 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{e.label}</Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: e.color }}>{e.count}</Typography>
                </Stack>
                <Box sx={{ height: 7, bgcolor: MP.bg, borderRadius: 99 }}>
                  <Box sx={{ width: `${e.pct}%`, height: '100%', bgcolor: e.color, borderRadius: 99 }} />
                </Box>
              </Box>
            ))}
          </Stack>
        </Grid>
      </Grid>

      {/* Complaint-level table */}
      <Box sx={{ p: 2.5 }}>
        <Typography sx={{ fontFamily: DISPLAY, fontSize: 13.5, fontWeight: 700, letterSpacing: '.02em', color: MP.dark, mb: 1.75 }}>
          Complaint-Level Detail{' '}
          <Box component="span" sx={{ fontFamily: "'Outfit', sans-serif", fontWeight: 400, fontSize: 12, color: MP.textMuted }}>
            (Showing {tableRows.length} of {rows.length})
          </Box>
        </Typography>
        <ScrollTable minW={1000} maxH={360}>
          <thead>
            <tr>
              <Th>Code</Th>
              <Th>Date Raised</Th>
              <Th>Source</Th>
              <Th>Visitor</Th>
              <Th>Branch</Th>
              <Th>Raised By</Th>
              <Th align="center">Response TAT</Th>
              <Th align="center">Resolution TAT</Th>
              <Th>Status</Th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map(({ r, responseMins, resolutionMins }) => {
              const statusLabel = String(r?.view_status_label || '').toLowerCase();
              const responseTone   = responseMins == null ? 'yellow' : responseMins <= 120 ? 'green' : responseMins <= 360 ? 'orange' : 'red';
              const resolutionTone = resolutionMins == null ? 'yellow' : resolutionMins <= 1440 ? 'green' : 'orange';
              return (
                <Tr key={r.nc_id || r.code}>
                  <Td sx={{ fontFamily: MONO, fontSize: 12, color: MP.tealDark }}>{r.code || '—'}</Td>
                  <Td sx={{ fontFamily: MONO, fontSize: 11, whiteSpace: 'nowrap' }}>
                    {r.created_at
                      ? new Date(r.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' })
                      : '—'}
                  </Td>
                  <Td sx={{ fontSize: 12 }}>{r.source || '—'}</Td>
                  <Td sx={{ fontFamily: MONO, fontSize: 11, color: MP.textMuted }}>{r.visitor_id || '—'}</Td>
                  <Td sx={{ fontSize: 12 }}>{r.branch || '—'}</Td>
                  <Td sx={{ fontSize: 12 }}>{r.raised_by?.name || '—'}</Td>
                  <Td sx={{ textAlign: 'center' }}>
                    {responseMins == null
                      ? <Box sx={{ color: MP.textMuted, fontSize: 12 }}>—</Box>
                      : <Pill tone={responseTone}>{formatMinutes(responseMins)}</Pill>}
                  </Td>
                  <Td sx={{ textAlign: 'center' }}>
                    {resolutionMins == null
                      ? <Box sx={{ color: MP.textMuted, fontSize: 12 }}>—</Box>
                      : <Pill tone={resolutionTone}>{formatMinutes(resolutionMins)}</Pill>}
                  </Td>
                  <Td><StatusBadge>{titleCase(statusLabel) || '—'}</StatusBadge></Td>
                </Tr>
              );
            })}
          </tbody>
        </ScrollTable>
      </Box>
    </Box>
  );
};

// Title-case for status labels — "investigating" → "Investigating".
const titleCase = (s) => s ? s.charAt(0).toUpperCase() + s.slice(1) : '';

// ── Small atoms ──────────────────────────────────────────────
const LegendSwatch = ({ color, children }) => (
  <Stack direction="row" alignItems="center" spacing={0.625} sx={{ fontSize: 11 }}>
    <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: color }} />
    <span>{children}</span>
  </Stack>
);

const filterInputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: MP.white,
    borderRadius: '6px',
    fontSize: 13,
    fontFamily: "'Outfit', sans-serif",
    '& fieldset': { borderColor: MP.border, borderWidth: 1.5 },
    '&:hover fieldset': { borderColor: MP.teal },
    '&.Mui-focused fieldset': { borderColor: MP.teal, borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': { fontSize: 12 },
};

export default Analytics;
